import React, { useState } from 'react';
import PrivacyPolicyModal from '../PrivacyPolicyModal';

interface PrivacyConsentsSectionProps {
  consents: {
    dataProcessing: boolean;
    promotionalTraditional: boolean;
    promotionalElectronic: boolean;
  };
  errors: Record<string, string>;
  onChange: (field: string, value: boolean) => void;
}

const PrivacyConsentsSection: React.FC<PrivacyConsentsSectionProps> = ({
  consents,
  errors,
  onChange
}) => {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  return (
    <>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm ring-1 ring-black/5 dark:ring-white/10 p-6 sm:p-8">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Consensi Privacy
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Ai sensi del Regolamento (UE) 679/2016 (GDPR)
        </p>

        {/* Link per leggere l'informativa completa */}
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <button
            type="button"
            onClick={() => setShowPrivacyModal(true)}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium text-sm flex items-center gap-2 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Leggi l'informativa privacy completa
          </button>
        </div>

        <div className="space-y-6">
          {/* Consenso obbligatorio al trattamento dati */}
          <div className={`p-4 rounded-lg border-2 ${
            errors.dataProcessing 
              ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
              : 'border-gray-200 dark:border-gray-700'
          }`}>
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={consents.dataProcessing}
                onChange={(e) => onChange('dataProcessing', e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                <span className="font-semibold text-gray-900 dark:text-white">
                  Consenso al trattamento dei dati personali *
                </span>
                <br />
                Dichiaro di aver letto l'informativa privacy e acconsento al trattamento dei miei dati personali 
                per le finalità di perseguimento degli scopi statutari e svolgimento delle finalità associative, 
                come descritto nell'informativa ai sensi dell'art. 13 del Regolamento (UE) 679/2016.
              </span>
            </label>
            {errors.dataProcessing && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400 ml-8">
                {errors.dataProcessing}
              </p>
            )}
          </div>

          {/* Consenso facoltativo - strumenti tradizionali */}
          <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={consents.promotionalTraditional}
                onChange={(e) => onChange('promotionalTraditional', e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                <span className="font-semibold text-gray-900 dark:text-white">
                  Consenso finalità promozionali - strumenti tradizionali
                </span>
                <br />
                Acconsento al trattamento dei miei dati personali per finalità di informazione e promozioni 
                commerciali di prodotti e servizi promossi dal Titolare attraverso strumenti tradizionali 
                (posta cartacea, chiamata con operatore).
              </span>
            </label>
          </div>

          {/* Consenso facoltativo - strumenti elettronici */}
          <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={consents.promotionalElectronic}
                onChange={(e) => onChange('promotionalElectronic', e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                <span className="font-semibold text-gray-900 dark:text-white">
                  Consenso finalità promozionali - strumenti elettronici
                </span>
                <br />
                Acconsento al trattamento dei miei dati personali per finalità di informazione e promozioni 
                commerciali di prodotti e servizi promossi dal Titolare attraverso strumenti di comunicazione 
                elettronica (e-mail, fax, SMS, MMS, APP, social media).
              </span>
            </label>
          </div>
        </div>

        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-sm text-yellow-800 dark:text-yellow-300">
              <p className="font-medium">Nota importante:</p>
              <p className="mt-1">
                Il consenso al trattamento dei dati personali (primo consenso) è obbligatorio per procedere 
                con l'iscrizione. Gli altri consensi sono facoltativi e possono essere revocati in qualsiasi momento.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modale informativa privacy */}
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </>
  );
};

export default PrivacyConsentsSection;

